import { ActiveDirectory } from "../pages/ActiveDirectory";
import { AppLayout } from "../pages/AppLayout";
import { BaseLayout } from "../pages/BaseLayout";
import { Configuration } from "../pages/Configuration";
import { Home } from "../pages/Home";
import { Login } from "../pages/Login";
import { Navigate } from "react-router-dom";
import { NotFound } from "../pages/NotFound";
import { UploadedFiles } from "../pages/UploadedFiles";
import { Users } from "../pages/Users";
import { UploadSettings } from "../pages/UploadSettings";
import { Sidebar } from "./sidebar/Sidebar";
import { ConfigSidebar } from "./sidebar/ConfigSidebar";
import { Disabled } from "../pages/Disabled";
import React, { lazy } from "react";
import { LazyWrapper } from "./LazyWrapper";
import { CirromMonitorSidebar } from "./sidebar/CirromMonitorSidebar";
import { TempReport } from "./monitor/TempReport";
import { CirromContent } from "../pages/CirromContent";
import { CirromDashboard } from "../pages/CirromDashboard";
import { ValidateDashboard } from "../pages/ValidateDashboard";
import { AllDataProjects } from "../pages/AllDataProjects";
import { createCommonRoutes } from "./routing/routeFactories";
import type { RouteContext } from "./routing/routeTypes";
import {
  getPreferredUI,
  getPreferredDashboardPath,
} from "../utils/uiPreference";

const ContentDashboard = lazy(() =>
  import("../pages/content/ContentDashboard").then((module) => ({
    default: module.ContentDashboard,
  }))
);

const MailboxDashboard = lazy(() =>
  import("../pages/mailbox/MailboxDashboard").then((module) => ({
    default: module.MailboxDashboard,
  }))
);

const ListManagement = lazy(() =>
  import("../pages/list-management/ListManagement").then((module) => ({
    default: module.ListManagement,
  }))
);

const ContentAuthorsPage = lazy(() =>
  import("../pages/content/ContentAuthorsPage").then((module) => ({
    default: module.ContentAuthorsPage,
  }))
);

const ContentSubscribersPage = lazy(() =>
  import("../pages/content/ContentSubscribersPage").then((module) => ({
    default: module.ContentSubscribersPage,
  }))
);

const ContentArticlesPage = lazy(() =>
  import("../pages/content/ContentArticlesPage").then((module) => ({
    default: module.ContentArticlesPage,
  }))
);

// Lazy load configuration pages (moved from direct imports)
const Deployments = lazy(() =>
  import("../pages/Deployments").then((module) => ({
    default: module.Deployments,
  }))
);

const SecretsAndFlagsPage = lazy(() =>
  import("../pages/Secrets").then((module) => ({
    default: module.SecretsAndFlagsPage,
  }))
);
const SupportPage = lazy(() =>
  import("../pages/Support").then((module) => ({ default: module.SupportPage }))
);

// Lazy load data-quality pages
const ChangeTicketsPage = lazy(() =>
  import("../pages/data-quality/ChangeTickets").then((module) => ({
    default: module.ChangeTicketsPage,
  }))
);
const ChangedFieldsPage = lazy(() =>
  import("../pages/data-quality/ChangedFields").then((module) => ({
    default: module.ChangedFieldsPage,
  }))
);
const IntegratedSystemsRules = lazy(() =>
  import("../pages/data-quality/IntegratedSystemsRules").then((module) => ({
    default: module.IntegratedSystemsRules,
  }))
);
const DuplicatesPage = lazy(() =>
  import("../pages/data-quality/Duplicates").then((module) => ({
    default: module.DuplicatesPage,
  }))
);
const RuleEvaluation = lazy(() =>
  import("../pages/data-quality/RuleEvaluation").then((module) => ({
    default: module.RuleEvaluation,
  }))
);
const Accounts = lazy(() =>
  import("../pages/data-quality/Accounts").then((module) => ({
    default: module.Accounts,
  }))
);
const DataProjectPage = lazy(() =>
  import("../pages/data-quality/DataProject").then((module) => ({
    default: module.DataProjectPage,
  }))
);
const NameChangesPage = lazy(() =>
  import("../pages/data-quality/NameChanges").then((module) => ({
    default: module.NameChangesPage,
  }))
);

const EmailChangesPage = lazy(() =>
  import("../pages/data-quality/EmailChanges").then((module) => ({
    default: module.EmailChangesPage,
  }))
);
const LocationChangesPage = lazy(() =>
  import("../pages/data-quality/LocationChanges").then((module) => ({
    default: module.LocationChangesPage,
  }))
);
const EmploymentChangesPage = lazy(() =>
  import("../pages/data-quality/EmploymentChanges").then((module) => ({
    default: module.EmploymentChangesPage,
  }))
);
const DataProjects = lazy(() =>
  import("../pages/data-quality/DataProjects").then((module) => ({
    default: module.DataProjects,
  }))
);
const RejectedFieldsPage = lazy(() =>
  import("../pages/data-quality/RejectedFields").then((module) => ({
    default: module.RejectedFieldsPage,
  }))
);

const MonthlyReport = lazy(() =>
  import("../pages/data-quality/MonthlyReport").then((module) => ({
    default: module.MonthlyReport,
  }))
);
const Addresses = lazy(() =>
  import("../pages/data-quality/Addresses").then((module) => ({
    default: module.Addresses,
  }))
);
const DataProjectAlerts = lazy(() =>
  import("../pages/data-quality/DataProjectAlerts").then((module) => ({
    default: module.DataProjectAlerts,
  }))
);
const AlertResults = lazy(() =>
  import("../pages/data-quality/AlertResults").then((module) => ({
    default: module.AlertResults,
  }))
);
const MailboxDataPage = lazy(() =>
  import("../pages/data-quality/MailboxData").then((module) => ({
    default: module.MailboxDataPage,
  }))
);
const EnrichServicesPage = lazy(() =>
  import("../pages/EnrichServices").then((module) => ({
    default: module.EnrichServicesPage,
  }))
);

// Lazy load sync pages
const CirromSyncPage = lazy(() =>
  import("../pages/sync/Sync").then((module) => ({
    default: module.CirromSyncPage,
  }))
);
const ContactsSyncLinkagePage = lazy(() =>
  import("../pages/sync/linkage/variants/ContactsSyncLinkage").then(
    (module) => ({
      default: module.ContactsSyncLinkagePage,
    })
  )
);
const CompaniesSyncLinkagePage = lazy(() =>
  import("../pages/sync/linkage/variants/CompaniesSyncLinkage").then(
    (module) => ({
      default: module.CompaniesSyncLinkagePage,
    })
  )
);
const CampaignSyncLinkagePage = lazy(() =>
  import("../pages/sync/linkage/variants/CampaignSyncLinkage").then(
    (module) => ({
      default: module.CampaignSyncLinkagePage,
    })
  )
);

const MailboxContactSyncLinkagePage = lazy(() =>
  import("../pages/sync/linkage/variants/MailboxContactSyncLinkage").then(
    (module) => ({
      default: module.MailboxContactSyncLinkage,
    })
  )
);
const SubscriberSyncLinkagePage = lazy(() =>
  import("../pages/sync/linkage/variants/SubscriberSyncLinkage").then(
    (module) => ({
      default: module.SubscriberSyncLinkagePage,
    })
  )
);
const SyncUnmatchedPage = lazy(() =>
  import("../pages/sync/SyncUnmatched").then((module) => ({
    default: module.SyncUnmatchedPage,
  }))
);
const SyncFieldMatchingPage = lazy(() =>
  import("../pages/sync/SyncFieldMatching").then((module) => ({
    default: module.SyncFieldMatchingPage,
  }))
);

const AllSyncIntegrations = lazy(() =>
  import("../pages/sync/AllSyncIntegrations").then((module) => ({
    default: module.AllSyncIntegrations,
  }))
);
const EventSyncDashboardPage = lazy(() =>
  import("../pages/sync/EventSyncDashboard").then((module) => ({
    default: module.EventSyncDashboardPage,
  }))
);

// Lazy load enrich pages
const EnrichProjectDashboardPage = lazy(() =>
  import("../pages/enrich/EnrichProjectDashboard").then((module) => ({
    default: module.EnrichProjectDashboardPage,
  }))
);
const EnrichProjectInScopePage = lazy(() =>
  import("../pages/enrich/EnrichProjectInScope").then((module) => ({
    default: module.EnrichProjectInScopePage,
  }))
);
const EnrichProjectLinkagePage = lazy(() =>
  import("../pages/enrich/EnrichProjectLinkage").then((module) => ({
    default: module.EnrichProjectLinkagePage,
  }))
);
const EnrichProjectDifferencesPage = lazy(() =>
  import("../pages/enrich/EnrichProjectDifferences").then((module) => ({
    default: module.EnrichProjectDifferencesPage,
  }))
);
const EnrichProjectAuditHistoryPage = lazy(() =>
  import("../pages/enrich/EnrichProjectAuditHistory").then((module) => ({
    default: module.EnrichProjectAuditHistoryPage,
  }))
);

// Lazy load monitor pages
const CirromMonitor = lazy(() =>
  import("../pages/monitor/CirromMonitor").then((module) => ({
    default: module.CirromMonitor,
  }))
);
const CirromMonitorRedirectToFirstItem = lazy(() =>
  import("../pages/monitor/CirromMonitor").then((module) => ({
    default: module.CirromMonitorRedirectToFirstItem,
  }))
);
const CirromMonitorLinkage = lazy(() =>
  import("../pages/monitor/CirromMonitorLinkage").then((module) => ({
    default: module.CirromMonitorLinkage,
  }))
);
const MonitorProjectPage = lazy(() =>
  import("../pages/monitor/MonitorProjectPage").then((module) => ({
    default: module.MonitorProjectPage,
  }))
);
const MonitorNameChangesPage = lazy(() =>
  import("../pages/monitor/MonitorNameChanges").then((module) => ({
    default: module.MonitorNameChangesPage,
  }))
);
const MonitorEmploymentChangesPage = lazy(() =>
  import("../pages/monitor/MonitorEmploymentChanges").then((module) => ({
    default: module.MonitorEmploymentChangesPage,
  }))
);
const MonitorLocationChangesPage = lazy(() =>
  import("../pages/monitor/MonitorLocationChanges").then((module) => ({
    default: module.MonitorLocationChangesPage,
  }))
);
const MonitorAlerts = lazy(() =>
  import("../pages/monitor/MonitorAlerts").then((module) => ({
    default: module.MonitorAlerts,
  }))
);

// Lazy load additional pages
const AccountDuplicatesPage = lazy(() =>
  import("../pages/data-quality/AccountDuplicates").then((module) => ({
    default: module.AccountDuplicatesPage,
  }))
);
const JobTitleConfig = lazy(() =>
  import("../pages/JobTitleConfig").then((module) => ({
    default: module.JobTitleConfig,
  }))
);

const getAccessPathForLoggedInUser = (originalPath: string) => {
  if (window.location.pathname === "/404") {
    return;
  }
  if (["/active-directory", "/login", "/"].indexOf(originalPath) > -1) {
    return getPreferredDashboardPath();
  }
  return originalPath;
};

/**
 * Route set up
 * @param isLoggedIn
 * @param originalAccessPath
 * @param userProfile
 * @param integratedSystemsForUser
 * @param dataProjects
 * @param fetchDataProjects
 * @param selectedSystemId
 * @param setSelectedSystemId
 * @constructor
 */
export const Routes = (
  isLoggedIn,
  originalAccessPath,
  userProfile,
  integratedSystemsForUser,
  dataProjects,
  fetchDataProjects,
  selectedSystemId,
  setSelectedSystemId
) => {
  // Create context object for route factories
  const context: RouteContext = {
    isLoggedIn,
    originalAccessPath,
    userProfile,
    integratedSystemsForUser,
    dataProjects,
    fetchDataProjects,
    selectedSystemId,
    setSelectedSystemId,
  };

  return [
    {
      path: "/product",
      element: isLoggedIn ? (
        <CirromContent
          integratedSystemsForUser={integratedSystemsForUser}
          dataProjects={dataProjects}
          selectedSystemId={selectedSystemId}
          setSelectedSystemId={setSelectedSystemId}
          isDataProject={false}
        />
      ) : (
        <Navigate to="/login" state={{ hasRedirect: true }} />
      ),
      children: [
        // Routes without :integratedSystemId parameter
        {
          path: "/product/dashboard/",
          element: (
            <CirromDashboard
              userSystems={integratedSystemsForUser}
              setSelectedSystemId={setSelectedSystemId}
            />
          ),
        },
        {
          path: "/product/validate/",
          element: (
            <ValidateDashboard
              userSystems={integratedSystemsForUser}
              setSelectedSystemId={setSelectedSystemId}
            />
          ),
        },

        // Content dashboard routes
        {
          path: "/product/content/:syncGroupId",
          element: (
            <LazyWrapper>
              <ContentDashboard setSelectedSystemId={setSelectedSystemId} />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/mailbox/:syncGroupId",
          element: (
            <LazyWrapper>
              <MailboxDashboard setSelectedSystemId={setSelectedSystemId} />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/content/authors/:syncGroupId",
          element: (
            <LazyWrapper>
              <ContentAuthorsPage setSelectedSystemId={setSelectedSystemId} />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/content/subscribers/:syncGroupId",
          element: (
            <LazyWrapper>
              <ContentSubscribersPage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/content/articles/:syncGroupId",
          element: (
            <LazyWrapper>
              <ContentArticlesPage setSelectedSystemId={setSelectedSystemId} />
            </LazyWrapper>
          ),
        },

        // Data projects management routes
        {
          path: "/product/data-projects/:integratedSystemId",
          element: (
            <LazyWrapper>
              <AllDataProjects
                dataProjects={dataProjects}
                setSelectedSystemId={setSelectedSystemId}
                fetchDataProjects={fetchDataProjects}
                dataProjectType="data_quality"
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/data-projects/monitor/:integratedSystemId",
          element: (
            <LazyWrapper>
              <AllDataProjects
                dataProjects={dataProjects}
                setSelectedSystemId={setSelectedSystemId}
                fetchDataProjects={fetchDataProjects}
                dataProjectType="monitor"
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/data-projects/sync/:integratedSystemId",
          element: (
            <LazyWrapper>
              <AllDataProjects
                dataProjects={dataProjects}
                setSelectedSystemId={setSelectedSystemId}
                fetchDataProjects={fetchDataProjects}
                dataProjectType="sync"
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/data-projects/enrich/:integratedSystemId/:enrichSystemId",
          element: (
            <LazyWrapper>
              <AllDataProjects
                dataProjects={dataProjects}
                setSelectedSystemId={setSelectedSystemId}
                fetchDataProjects={fetchDataProjects}
                dataProjectType="enrich"
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/data-projects/enrich/:integratedSystemId",
          element: (
            <LazyWrapper>
              <AllDataProjects
                dataProjects={dataProjects}
                setSelectedSystemId={setSelectedSystemId}
                fetchDataProjects={fetchDataProjects}
                dataProjectType="enrich"
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/enrich/:integratedSystemId",
          element: (
            <LazyWrapper>
              <EnrichServicesPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        // List management (hidden from sidebar, accessed by direct URL)
        {
          path: "/product/list-management/:integratedSystemId/:batchGroupId",
          element: (
            <LazyWrapper>
              <ListManagement
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        //sync-integrations
        {
          path: "/product/sync-integrations",
          element: (
            <LazyWrapper>
              <AllSyncIntegrations />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/sync/events/:syncGroupId",
          element: (
            <LazyWrapper>
              <EventSyncDashboardPage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        //sync-linkage - to move to standalone package once fully fleshed
        {
          path: "/product/sync/linkage/:integratedSystemId/contacts",
          element: (
            <LazyWrapper>
              <ContactsSyncLinkagePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/sync/linkage/:integratedSystemId/campaigns",
          element: (
            <LazyWrapper>
              <CampaignSyncLinkagePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/product/sync/linkage/:integratedSystemId/mailbox-contacts",
          element: (
            <LazyWrapper>
              <MailboxContactSyncLinkagePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/product/sync/linkage/:integratedSystemId/subscribers",
          element: (
            <LazyWrapper>
              <SubscriberSyncLinkagePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/product/sync/linkage/:integratedSystemId/:dataType",
          element: (
            <LazyWrapper>
              <CompaniesSyncLinkagePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        // All common routes with :integratedSystemId
        ...createCommonRoutes({
          basePath: "/product",
          userSystems: integratedSystemsForUser,
          isDataProject: false,
          context,
          setSelectedSystemId,
          includeContactMonitorRedirect: true,
        }),
      ],
    },
    {
      path: "/data-project",
      element: isLoggedIn ? (
        <CirromContent
          integratedSystemsForUser={integratedSystemsForUser}
          dataProjects={dataProjects}
          selectedSystemId={selectedSystemId}
          setSelectedSystemId={setSelectedSystemId}
          isDataProject={true}
        />
      ) : (
        <Navigate to="/login" state={{ hasRedirect: true }} />
      ),
      children: [
        {
          path: "/data-project/enrich/:dataProjectId/in-scope",
          element: (
            <LazyWrapper>
              <EnrichProjectInScopePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/data-project/enrich/:dataProjectId/linkage/:linkageStatus",
          element: (
            <LazyWrapper>
              <EnrichProjectLinkagePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/data-project/enrich/:dataProjectId/differences",
          element: (
            <LazyWrapper>
              <EnrichProjectDifferencesPage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/data-project/enrich/:dataProjectId/audit-history",
          element: (
            <LazyWrapper>
              <EnrichProjectAuditHistoryPage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/data-project/enrich/:dataProjectId",
          element: (
            <LazyWrapper>
              <EnrichProjectDashboardPage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        // All common routes with :integratedSystemId for data projects
        ...createCommonRoutes({
          basePath: "/data-project",
          userSystems: dataProjects,
          isDataProject: true,
          context,
          setSelectedSystemId,
          includeContactMonitorRedirect: false,
        }),
      ],
    },
    {
      path: "/product/configure",
      element: isLoggedIn ? (
        <CirromContent
          integratedSystemsForUser={integratedSystemsForUser}
          dataProjects={dataProjects}
          selectedSystemId={selectedSystemId}
          setSelectedSystemId={setSelectedSystemId}
          isDataProject={true}
        />
      ) : (
        <Navigate to="/login" state={{ hasRedirect: true }} />
      ),
      children: [
        {
          path: "/product/configure/users",
          element: <Users userSystems={integratedSystemsForUser} />,
        },
        {
          path: "/product/configure/sources",
          element: <Configuration />,
        },
        {
          path: "/product/configure/secrets",
          element: (
            <LazyWrapper>
              <SecretsAndFlagsPage
                integratedSystems={integratedSystemsForUser}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/configure/job-title-config",
          element: (
            <LazyWrapper>
              <JobTitleConfig />
            </LazyWrapper>
          ),
        },
        {
          path: "/product/configure/support",
          element: (
            <LazyWrapper>
              <SupportPage />
            </LazyWrapper>
          ),
        },
      ],
    },
    {
      path: "/app/configure",
      element: isLoggedIn ? (
        <AppLayout
          Sidebar={ConfigSidebar}
          userProfile={userProfile}
          selectedSystemId={selectedSystemId}
          setSelectedSystemId={setSelectedSystemId}
          userSystems={integratedSystemsForUser}
        />
      ) : (
        <Navigate to="/login" state={{ hasRedirect: true }} />
      ),
      children: [
        { path: "/app/configure", element: <Configuration /> },
        { path: "/app/configure/batch", element: <Configuration /> },
        { path: "/app/configure/upload-settings", element: <UploadSettings /> },
        {
          path: "/app/configure/system-rules/",
          element: (
            <LazyWrapper>
              <IntegratedSystemsRules />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/configure/data-project-alerts/",
          element: (
            <LazyWrapper>
              <DataProjectAlerts />
            </LazyWrapper>
          ),
        },

        {
          path: "/app/configure/users",
          element: <Users userSystems={integratedSystemsForUser} />,
        },
        {
          path: "/app/configure/deployments",
          element: (
            <LazyWrapper>
              <Deployments />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/configure/rule-evaluation/:batchGroupId/:ruleId",
          element: (
            <LazyWrapper>
              <RuleEvaluation />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/configure/secrets",
          element: (
            <LazyWrapper>
              <SecretsAndFlagsPage
                integratedSystems={integratedSystemsForUser}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/configure/job-title-config",
          element: (
            <LazyWrapper>
              <JobTitleConfig />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/configure/support",
          element: (
            <LazyWrapper>
              <SupportPage />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/configure/data-project-alerts/results/:dataAlertId",
          element: (
            <LazyWrapper>
              <AlertResults />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/configure/mailbox-data/:integratedSystemId",
          element: (
            <LazyWrapper>
              <MailboxDataPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        { path: "*", element: <Navigate to="/404" /> },
      ],
    },
    {
      path: "/app/contact-monitor",
      element: isLoggedIn ? (
        <AppLayout
          Sidebar={CirromMonitorSidebar}
          userProfile={userProfile}
          selectedSystemId={selectedSystemId}
          setSelectedSystemId={setSelectedSystemId}
          userSystems={integratedSystemsForUser}
        />
      ) : (
        <Navigate to="/login" state={{ hasRedirect: true }} />
      ),
      children: [
        {
          path: "/app/contact-monitor",
          element: (
            <LazyWrapper>
              <CirromMonitorRedirectToFirstItem
                userSystems={integratedSystemsForUser}
                isDataProject={false}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/contact-monitor/data-project/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <MonitorProjectPage
                userSystems={dataProjects}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/contact-monitor/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <CirromMonitor
                userSystems={integratedSystemsForUser}
                isDataProject={false}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/contact-monitor/data-project/alerts/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <MonitorAlerts
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/contact-monitor/data-project/linking/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <CirromMonitorLinkage
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/contact-monitor/data-project/name-changes/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <MonitorNameChangesPage
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/contact-monitor/data-project/employment-changes/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <MonitorEmploymentChangesPage
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/contact-monitor/data-project/location-changes/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <MonitorLocationChangesPage
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/contact-monitor/name-changes/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <MonitorNameChangesPage
                userSystems={dataProjects}
                isDataProject={false}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
      ],
    },
    {
      path: "/app",
      element: isLoggedIn ? (
        <AppLayout
          Sidebar={Sidebar}
          userProfile={userProfile}
          selectedSystemId={selectedSystemId}
          setSelectedSystemId={setSelectedSystemId}
          userSystems={integratedSystemsForUser}
        />
      ) : (
        <Navigate to="/login" state={{ hasRedirect: true }} />
      ),
      children: [
        {
          path: "/app/dashboard",
          element:
            getPreferredUI() === "product" ? (
              <Navigate to="/product/dashboard" replace />
            ) : (
              <Home
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            ),
        },
        {
          path: "/app/dashboard/:integratedSystemId",
          element: (
            <Home
              userSystems={integratedSystemsForUser}
              setSelectedSystemId={setSelectedSystemId}
            />
          ),
        },
        {
          path: "/app/data-projects/:integratedSystemId",
          element: (
            <LazyWrapper>
              <DataProjects
                dataProjects={dataProjects}
                fetchDataProjects={fetchDataProjects}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/app/contact-monitor/report/:integratedSystemId/*",
          element: (
            <TempReport
              userSystems={dataProjects}
              setSelectedSystemId={setSelectedSystemId}
            />
          ),
        },
        {
          path: "/app/data-project/:dataProjectId",
          element: (
            <LazyWrapper>
              <DataProjectPage
                dataProjects={dataProjects}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        { path: "/app/my-data", element: <UploadedFiles /> },
        { path: "/app/my-data/:id", element: <UploadedFiles /> },

        {
          path: "/app/change-tickets/:integratedSystemId",
          element: (
            <LazyWrapper>
              <ChangeTicketsPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/app/crm/name-changes/:integratedSystemId",
          element: (
            <LazyWrapper>
              <NameChangesPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/data-project/name-changes/:integratedSystemId",
          element: (
            <LazyWrapper>
              <NameChangesPage
                userSystems={dataProjects}
                isDataProject={true}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/crm/email-changes/:integratedSystemId",
          element: (
            <LazyWrapper>
              <EmailChangesPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/data-project/email-changes/:integratedSystemId",
          element: (
            <LazyWrapper>
              <EmailChangesPage
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/app/crm/location-changes/:integratedSystemId",
          element: (
            <LazyWrapper>
              <LocationChangesPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/data-project/location-changes/:integratedSystemId",
          element: (
            <LazyWrapper>
              <LocationChangesPage
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/crm/employment-changes/:integratedSystemId",
          element: (
            <LazyWrapper>
              <EmploymentChangesPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/data-project/employment-changes/:integratedSystemId",
          element: (
            <LazyWrapper>
              <EmploymentChangesPage
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/app/changed-fields/:integratedSystemId",
          element: (
            <LazyWrapper>
              <ChangedFieldsPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/rejected-fields/:integratedSystemId",
          element: (
            <LazyWrapper>
              <RejectedFieldsPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/app/rejected-fields/:integratedSystemId",
          element: (
            <LazyWrapper>
              <RejectedFieldsPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/change-tickets/:integratedSystemId/:selectedTab",
          element: (
            <LazyWrapper>
              <ChangeTicketsPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/changed-fields/:integratedSystemId/:selectedTab",
          element: (
            <LazyWrapper>
              <ChangedFieldsPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/duplicates/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <DuplicatesPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/data-project/duplicates/:integratedSystemId/*",
          element: (
            <LazyWrapper>
              <DuplicatesPage
                userSystems={dataProjects}
                isDataProject
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
          exact: true,
        },
        {
          path: "/app/account-duplicates/:integratedSystemId",
          element: (
            <LazyWrapper>
              <AccountDuplicatesPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/app/accounts/:integratedSystemId",
          element: (
            <LazyWrapper>
              <Accounts
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/addresses/:integratedSystemId",
          element: (
            <LazyWrapper>
              <Addresses
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },

        {
          path: "/app/sync/linkage/:integratedSystemId/contacts",
          element: (
            <LazyWrapper>
              <ContactsSyncLinkagePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/sync/linkage/:integratedSystemId/:dataType",
          element: (
            <LazyWrapper>
              <CompaniesSyncLinkagePage
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/sync/unmatched/:integratedSystemId/:dataType",
          element: (
            <LazyWrapper>
              <SyncUnmatchedPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/sync/fieldmatching/:integratedSystemId/:dataType",
          element: (
            <LazyWrapper>
              <SyncFieldMatchingPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/sync/:integratedSystemId",
          element: (
            <LazyWrapper>
              <CirromSyncPage
                userSystems={integratedSystemsForUser}
                setSelectedSystemId={setSelectedSystemId}
              />
            </LazyWrapper>
          ),
        },
        {
          path: "/app/report",
          element: (
            <LazyWrapper>
              <MonthlyReport />
            </LazyWrapper>
          ),
        },

        { path: "*", element: <Navigate to="/404" /> },
      ],
    },
    {
      path: "/",
      element: !isLoggedIn ? (
        <BaseLayout />
      ) : (
        getAccessPathForLoggedInUser(originalAccessPath) && (
          <Navigate to={getAccessPathForLoggedInUser(originalAccessPath)} />
        )
      ),
      children: [
        {
          path: "/disabled",
          element: <Disabled />,
        },
        {
          path: "/login",
          element: <Login />,
        },
        {
          path: "/",
          element: <Navigate to="/login" state={{ hasRedirect: true }} />,
        },
        {
          path: "/active-directory",
          element: <ActiveDirectory />,
        },
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" /> },
      ],
    },
  ];
};
